import * as Sentry from "@sentry/react";
import Agent from "pages/agent";
import AgentGroups from "pages/agent-groups";
import Agents from "pages/agents";
import Anomalies from "pages/anomalies";
import Anomaly from "pages/anomaly";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import ComparisonTags from "pages/comparison-tags";
import Explore from "pages/explore";
import Interaction from "pages/interaction";
import Live from "pages/live";
import Navigation from "components-for/application/navigation";
import NoMatch from "pages/no-match";
import Page from "constants/page";
import Permissions from "pages/permissions";
import SpellingConfiguration from "pages/spelling-configuration";
import TagPhraseTriggers from "pages/tag-phrase-triggers";
import toast from "react-hot-toast";
import urlJoin from "url-join";
import Users from "pages/users";
import VerifyPermission from "components-for/pages/verify-permission";
import { axios, createAuthorisationHeader } from "utilities/axios";
import { ClientAuthenticatedRoutes } from "@edgetier/client-components";
import { deleteAccessToken, getAccessToken } from "utilities/access-token";
import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { IClient, IUser, Permission } from "@edgetier/watchtower-types";
import { initialiseTracking } from "utilities/track-user";
import { IProps } from "./authenticated-routes.types";
import { Route, Switch, useHistory } from "react-router-dom";
import { SpinnerUntil } from "@edgetier/components";
import { useClient } from "hooks-for/clients/use-client";
import { useUser } from "hooks-for/users/use-user";
import VerifyManageTagsPermission from "components-for/tags/verify-manage-tags-permission";
import CustomReports from "pages/custom-reports";
import MetabaseDashboard from "pages/custom-reports/metabase-dashboard";
import { TAG_COMPARISON_SCREEN_ENABLED } from "constants/feature-flags";
import { refreshToken } from "utilities/refresh-token";
import { setClientId } from "utilities/client-id";
import "./authenticated-routes.scss";

// TODO: Authentication failure not working. User isn't signed out.

const AuthenticatedRoutes: FunctionComponent<IProps> = () => {
    const history = useHistory();
    const accessToken = useMemo(() => getAccessToken(), []);
    const [hasAccessToken, setHasAccessToken] = useState(typeof accessToken === "string");

    /**
     * If there's an authentication problem, delete any access token and redirect the user to the login screen.
     */
    const onAuthenticationFailure = useCallback(() => {
        deleteAccessToken();
        Sentry.configureScope((scope) => scope.setUser(null));
        history.push(Page.Login);
    }, [history]);

    // Request the user's details once they have an access token.
    const { data: user } = useUser({
        enabled: hasAccessToken,
        onError: onAuthenticationFailure,
        onSuccess: (user: IUser) => {
            Sentry.setUser({ userId: user.userId });

            setClientId(user.clientId);
            // Generate a new access token when the current one expires.
            createAuthRefreshInterceptor(axios, async (request) => {
                try {
                    await refreshToken();
                    request.response.config.headers["Authorization"] = createAuthorisationHeader();
                    return Promise.resolve();
                } catch {
                    toast.error("Authentication failed.");
                    onAuthenticationFailure();
                }
            });
        },
    });

    // Request the user's client.
    const { data: client, isLoading } = useClient(user?.clientId!, {
        enabled: typeof user !== "undefined",
        onError: onAuthenticationFailure,
        onSuccess: (client: IClient) => {
            const name = `${user?.firstName} ${user?.surname}`;
            initialiseTracking(user?.userId!, name, user?.emailAddress!, client.client);
        },
    });

    // On load, check if the user is signed in, and attempt to authenticate if not.
    useEffect(() => {
        const authenticate = async () => {
            try {
                if (typeof accessToken !== "string") {
                    await refreshToken();
                    setHasAccessToken(true);
                }
            } catch {
                onAuthenticationFailure();
            }
        };

        authenticate();
    }, [accessToken, onAuthenticationFailure]);

    return (
        <SpinnerUntil<IClient> data={client} isReady={!isLoading}>
            {client && (
                <ClientAuthenticatedRoutes>
                    <Navigation
                        isAgentReviewEnabled={client.isAgentReviewEnabled}
                        isExploreTagsEnabled={client.isExploreTagsEnabled}
                    />

                    <Switch>
                        {client.isAgentReviewEnabled && (
                            <Route exact={true} path={Page.Agent}>
                                <VerifyPermission permission={Permission.ViewAgent} isRoute>
                                    <Agent />
                                </VerifyPermission>
                            </Route>
                        )}

                        {client.isAgentReviewEnabled && (
                            <Route exact={true} path={Page.Agents}>
                                <VerifyPermission permission={Permission.ViewAgent} isRoute>
                                    <Agents />
                                </VerifyPermission>
                            </Route>
                        )}

                        <Route exact path={urlJoin(Page.Anomaly, ":anomalyId")}>
                            <VerifyPermission permission={Permission.ViewAnomalies} isRoute>
                                <Anomaly />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={Page.Anomalies}>
                            <VerifyPermission permission={Permission.ViewAnomalies} isRoute>
                                <Anomalies />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={Page.Explore}>
                            <VerifyPermission permission={Permission.ViewExplore} isRoute>
                                <Explore />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={urlJoin(Page.Interaction, ":interactionId")}>
                            <Interaction />
                        </Route>

                        {client.isLiveViewEnabled && (
                            <Route path={Page.Live}>
                                <VerifyPermission permission={Permission.ViewLive} isRoute>
                                    <Live />
                                </VerifyPermission>
                            </Route>
                        )}

                        <Route path={Page.Users}>
                            <VerifyPermission permission={Permission.ManageUsers} isRoute>
                                <Users />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={Page.Permissions}>
                            <VerifyPermission permission={Permission.ManageUsers} isRoute>
                                <Permissions />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={Page.SpellingConfiguration}>
                            <VerifyPermission permission={Permission.ManageWritingIssues} isRoute>
                                <SpellingConfiguration />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={urlJoin(Page.ManageTags, ":tagId?")}>
                            <VerifyManageTagsPermission isRoute>
                                <TagPhraseTriggers />
                            </VerifyManageTagsPermission>
                        </Route>

                        <Route exact path={urlJoin(Page.ManagePhraseTriggers, ":phraseTriggerId?")}>
                            <VerifyManageTagsPermission isRoute>
                                <TagPhraseTriggers />
                            </VerifyManageTagsPermission>
                        </Route>
                        <Route exact path={Page.PhraseTriggersReport}>
                            <VerifyManageTagsPermission isRoute>
                                <TagPhraseTriggers />
                            </VerifyManageTagsPermission>
                        </Route>

                        {client.isAgentReviewEnabled && (
                            <Route exact path={urlJoin(Page.AgentGroups, ":tagId?")}>
                                <VerifyPermission permission={Permission.ManageAgentTags} isRoute>
                                    <AgentGroups />
                                </VerifyPermission>
                            </Route>
                        )}

                        <Route exact path={urlJoin(Page.CustomReports, ":metabaseDashboardId")}>
                            <VerifyPermission permission={Permission.ViewCustomReports} isRoute>
                                <MetabaseDashboard />
                            </VerifyPermission>
                        </Route>

                        <Route exact path={Page.CustomReports}>
                            <VerifyPermission permission={Permission.ViewCustomReports} isRoute>
                                <CustomReports />
                            </VerifyPermission>
                        </Route>

                        {TAG_COMPARISON_SCREEN_ENABLED && client.isExploreTagsEnabled && (
                            <Route exact path={Page.ExploreTags}>
                                <VerifyPermission permission={Permission.ViewExplore} isRoute>
                                    <ComparisonTags />
                                </VerifyPermission>
                            </Route>
                        )}

                        <Route path="*">
                            <NoMatch />
                        </Route>
                    </Switch>
                </ClientAuthenticatedRoutes>
            )}
        </SpinnerUntil>
    );
};

export default memo(AuthenticatedRoutes);
